import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { Field } from "react-final-form";
import { Checkbox } from "./Checkbox";
import { useQuery } from "@apollo/client";

interface OptionProps {
  input: {
    value: any;
    [key: string]: any;
  };
  meta: any;
  label?: string;
  [key: string]: any;
}

function Option(props: OptionProps) {
  const {
    input: { value, ...restInput },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    meta,
    label,
    ...rest
  } = props;

  return (
    <FormControlLabel
      control={
        <Checkbox name={restInput.name} color="primary" value={String(value)} />
      }
      label={label}
      {...restInput}
      {...rest}
    />
  );
}

interface CheckboxOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  xs?: number;
}

interface CheckboxGroupQueryProps {
  query: any;
  variables?: any;
  name: string;
  label?: string;
  helperText?: string;
  maxHeight?: number;
}

function CheckboxGroupQuery({
  query,
  variables,
  ...props
}: CheckboxGroupQueryProps) {
  const { data } = useQuery(query, {
    variables,
  });

  const options = data?.options || [];

  return <CheckboxGroup options={options} {...props} />;
}

interface CheckboxGroupProps {
  options: CheckboxOption[];
  label?: string;
  helperText?: string;
  name: string;
  maxHeight?: number;
}

function CheckboxGroup({
  options,
  label,
  helperText,
  name,
  maxHeight = 500,
}: CheckboxGroupProps) {
  const [filter, setFilter] = useState("");

  const items = options.filter((option) =>
    option.label.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <FormControl
      component="fieldset"
      sx={{
        border: "1px solid",
        borderColor: "rgba(255, 255, 255, 0.23)",
        borderRadius: 1,
        padding: 2,
        alignItems: "center",
        "&:hover": {
          borderColor: "rgba(255, 255, 255, 1)",
        },

        maxHeight: maxHeight,
        overflow: "hidden",
        overflowY: "auto",
      }}
    >
      {label && (
        <FormLabel
          component="legend"
          sx={{
            float: "unset",
            display: "block",
            width: "auto",
            padding: "0 5px",
            height: "11px",
            fontSize: "0.75em",
            top: -3,
            maxWidth: "100%",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </FormLabel>
      )}
      <Grid container spacing={0}>
        {options.length > 10 && (
          <Grid size={12}>
            <TextField
              onChange={(e) => setFilter(e.target.value)}
              value={filter}
              label="Filter by name"
              fullWidth
              variant="standard"
            />
          </Grid>
        )}

        {items.map((option) => (
          <Grid key={option.value} size={option.xs || 12}>
            <Field
              name={name}
              type="checkbox"
              value={option.value}
              label={option.label}
              disabled={option.disabled}
              component={Option}
            />
          </Grid>
        ))}
      </Grid>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

interface CheckboxGroupFieldProps {
  query?: any;
  variables?: any;
  options?: CheckboxOption[];
  name: string;
  label?: string;
  helperText?: string;
  maxHeight?: number;
}

export function CheckboxGroupField({
  query,
  variables,
  ...props
}: CheckboxGroupFieldProps) {
  if (query) {
    return (
      <CheckboxGroupQuery query={query} variables={variables} {...props} />
    );
  }

  return <CheckboxGroup options={props.options || []} {...props} />;
}
